import { Component, OnInit, ViewChild, Output, EventEmitter } from '@angular/core';
import { SharedService } from '../../../layouts/shared-service';
import { ScheduleEmailComponent } from './schedule-email/schedule-email.component';
import { EmailTemplate } from 'src/app/models/email-template';
import { ScheduleEmailProps } from 'src/app/pages/group-analytics/group-analytics-email-pricing-dialog/schedule-email/schedule-email';
import { GroupAnalyticsGenerateDialogData } from '../group-analytics-generate-dialog/group-analytics-generate-dialog.component';
import { ValidationState } from './schedule-email/schedule-email-validation.service';

export enum EmailType{
    Scheduled = "Scheduled",
    RightNow = "RightNow"
}
export interface GroupAnalyticsEmailPricingDialogResult {
    emailTemplate: EmailTemplate;
    schedule?: ScheduleEmailProps;
    emailType: EmailType;
    sendNow?: boolean; // Flag to send email immediately in addition to scheduling
}

@Component({
    providers: [SharedService],
    selector: 'app-group-analytics-email-pricing-dialog',
    styleUrls: ['./group-analytics-email-pricing-dialog.component.scss'],
    templateUrl: './group-analytics-email-pricing-dialog.component.html',
})
export class GroupAnalyticsEmailPricingDialogComponent implements OnInit {
    @ViewChild(ScheduleEmailComponent) scheduleEmailComponent!: ScheduleEmailComponent;

    gridData: any[] = [];

    tabNames: { [key: string]: string } = {
        scheduleEmail: 'Schedule Email',
        customerEmails: 'Customer Emails',
    };

    get recipientList() {
        return this.gridData.filter(item => !item.isCustomer);
    }

    // Control disabling of action buttons based on schedule component state
    disableSendNow: boolean = true;
    disableSchedule: boolean = false;

    // Data property - will be set by dialog wrapper
    private _data: GroupAnalyticsGenerateDialogData;
    get data(): GroupAnalyticsGenerateDialogData {
        return this._data;
    }
    set data(value: GroupAnalyticsGenerateDialogData) {
        this._data = value;
        if (value && value.customers) {
            this.initializeGridData();
        }
    }

    // EventEmitters for dialog communication
    @Output() emailPricingResult = new EventEmitter<GroupAnalyticsEmailPricingDialogResult>();
    @Output() cancelDialog = new EventEmitter<void>();

    constructor() {}

    public ngOnInit(): void {
        // If data was already set by the wrapper before ngOnInit, ensure gridData is initialized
        if (this.data && this.data.customers && this.gridData.length === 0) {
            this.initializeGridData();
        }
    }

    private initializeGridData(): void {
        this.gridData = [];
        this.data.customers
            .filter((customer) => customer.contacts.length > 0)
            .forEach((customer) => {
                this.gridData.push({
                    ...customer,
                    isCustomer: true,
                });
                customer.contacts.forEach((contact) => {
                    this.gridData.push({
                        ...contact,
                        company: '',
                        isCustomer: false,
                    });
                });
            });
    }

    onScheduleStateChange(event: { disableSendNow: boolean; disableSchedule: boolean }): void {
        this.disableSendNow = event.disableSendNow;
        this.disableSchedule = event.disableSchedule;
    }

    onSendClick(): void {
        this.data.emailTemplate = this.getEmailTemplate();
        this.emailPricingResult.emit({
            emailTemplate: this.data.emailTemplate,
            schedule: null,
            emailType: EmailType.RightNow,
        });
    }
    onScheduleClick(): void {
        // Validate schedule to determine if sendNow flow should be enabled
        // (only for recurrent frequencies: Daily, Weekly, Monthly)
        const validationState: ValidationState = this.scheduleEmailComponent.validateSchedule();

        this.data.emailTemplate = this.getEmailTemplate();
        const scheduleValues: ScheduleEmailProps = this.scheduleEmailComponent.getScheduleValues();
        
        // Validation always succeeds for recurrent frequencies
        // shouldProceedAnyway (sendNow) is set based on whether datetime is in the past
        this.emailPricingResult.emit({
            emailTemplate: this.data.emailTemplate,
            schedule: scheduleValues,
            emailType: EmailType.Scheduled,
            sendNow: validationState.shouldProceedAnyway // Enable sendNow flow if datetime is in the past
        });
    }

    onCancelClick(): void {
        this.cancelDialog.emit();
    }

    private getEmailTemplate(): EmailTemplate {
        return {
            oid: this.data.emailTemplate.oid,
            emailContentHtml: this.data.emailTemplate.emailContentHtml,
            fromAddress: this.data.emailTemplate.fromAddress,
            replyTo: this.data.emailTemplate.replyTo,
            subject: this.data.emailTemplate.subject,
        };
    }
}
